import { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    IconButton,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    MenuItem,
} from "@mui/material";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";

import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import SearchIcon from "@mui/icons-material/Search";
import CustomDataGrid from "../../Components/CustomDataGrid"; // Adjust the import path as needed
import CustomFormDialog from "../../Components/CustomFormDialog";
import { columns, addItemFormFields, Get_URI } from "./config";
import { REACT_APP_BACKEND_URL } from "../../Utils/Language/urlBase";
import { toast } from "react-toastify";
import { Edit } from "@mui/icons-material";
import AddStockDialog from "../../Components/Dialogs/AddStock";
import EditDialog from "../../Components/Dialogs/EditDialog";
import { asyncDelay } from "../../Utils/functions";

const ItemList = () => {
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [selectedItemToEdit, setSelectedItemToEdit] = useState(null);
    const [isRefresh, setIsRefresh] = useState(false);
    const [isViewStock, setIsViewStock] = useState(false);
    const [stockToView, setStockToView] = useState(null);
    // Get the token using useAuthHeader
    const authHeader = useAuthHeader();
    const theme = useTheme();
    const [items, setItems] = useState([]);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [search, setSearch] = useState("");
    const [isAddItemQuantityOpen, setIsAddItemQuantityOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [otherParties, setOtherParties] = useState([]);
    const [stockLocations, setStockLocations] = useState([]);
    const [selectedStock, setSelectedStock] = useState(null);
    const [isEditStock, setIsEditStock] = useState(false);
    const [quantityToEdit, setQuantityToEdit] = useState(null); // (unused) consider removal later
    const [editStockForm, setEditStockForm] = useState({
        quantityInitial: "",
        pricePurchase: "",
        otherPartyId: "",
    });

    // central stock fetcher so we can reuse after edits
    const fetchStockForItem = async (itemId, { showDialog = true } = {}) => {
        if (!itemId) return;
        try {
            const response = await fetch(
                `${REACT_APP_BACKEND_URL}/stock/get?itemId=${itemId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authHeader,
                    },
                }
            );
            if (!response.ok) throw new Error("Failed fetching stock");
            const data = await response.json();
            setStockToView(data.stock);
            if (showDialog) setIsViewStock(true); // preserve previous behavior
        } catch (e) {
            toast.error("Couldn't refresh stock");
        }
    };

    const editStock = (newQuantity) => {
        console.log("edit stock");
    };

    // Function to handle changes in filter input fields

    const handleOpenDeleteDialog = (id) => {
        setSelectedRowId(id);
        setOpenDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
    };

    const deleteItem = async () => {
        try {
            const response = await fetch(
                Get_URI("DELETE", { id: selectedRowId }),
                {
                    method: "DELETE", // Use DELETE method instead of GET
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authHeader,
                    },
                }
            );
            if (response.ok) {
                setIsRefresh(!isRefresh);
                toast.success("Item deleted successfully");
            } else {
                throw new Error("Network response was not ok");
            }
        } catch (err) {
            toast.error("Something went wrong deleting item");
        }
    };

    const authUser = useAuthUser();
    console.log(authUser);
    useEffect(() => {
        if (isViewStock) {
            fetchStockForItem(selectedItem);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isViewStock, selectedItem]);

    const handleConfirmDelete = () => {
        deleteItem();
        setOpenDeleteDialog(false);
    };

    const actions = [
        {
            title: "Edit",
            color: "primary",
            handler: (row) => {
                const item = items.find((item) => item.id === row);
                setSelectedItemToEdit(item);
                setIsEditDialogOpen(true);
            },
            parameters: [
                "id",
                "name",
                "barcode",
                "priceNormal",
                "priceWholesale",
                "description",
            ],
            icon: <Edit />,
        },

        ...([1, 2].includes(authUser.employeeType.id)
            ? [
                  {
                      title: "View Stock",
                      color: "primary",
                      handler: (row) => {
                          setSelectedItem(row);
                          setIsViewStock(true);
                      },
                      parameters: ["id"],
                      icon: <RemoveRedEyeIcon />,
                  },
              ]
            : []),
        {
            title: "Add Stock",
            color: "primary",
            handler: (row) => {
                setSelectedItem(row);
                setIsAddItemQuantityOpen(true);
            },
            parameters: ["id"],
            icon: <AddIcon />,
        },
        {
            title: "Delete",
            color: "error",
            handler: handleOpenDeleteDialog,
            parameters: ["id"],
            icon: <DeleteIcon />,
        },
    ];

    // API call to get items with filters

    // Call getItems with filters
    const applyFilters = () => {
        setIsRefresh(!isRefresh);
    };

    const updateItem = async (row) => {
        // return true; // Return success for testing purpose
        console.log({
            selectedItem,
        });
        try {
            const response = await fetch(
                Get_URI("PATCH", { id: selectedItemToEdit.id }),
                {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authHeader,
                    },
                    body: JSON.stringify({
                        ...row,
                        minAlarmQuantity: Number(row.minAlarmQuantity),
                    }), // Send the row data
                }
            );
            if (response.ok) {
                setIsEditDialogOpen(false);
                setIsRefresh(!isRefresh);
                setSelectedItemToEdit(null);
                setSelectedItem(null);
                toast.success("Item updated successfully");
            }

            if (!response.ok) {
                throw new Error("Failed to update row");
            }

            return true; // Return success if the API call was successful
        } catch (error) {
            toast.error("Failed to update row");
        }
        /*
         */
    };

    useEffect(() => {
        const getItems = async (filters = {}) => {
            const queryParams = new URLSearchParams({
                isStock: true,
            }).toString(); // Convert filters object to query string

            try {
                const response = await fetch(
                    Get_URI("GET", { queryParams: queryParams }),
                    {
                        method: "GET",

                        headers: {
                            "Content-Type": "application/json",
                            Authorization: authHeader,
                        },
                    }
                );
                if (response.ok) {
                    const responseData = await response.json();
                    let filteredStock = responseData.data;
                    if (search) {
                        console.log(filters);
                        filteredStock = responseData.data.filter((item) =>
                            item.name
                                .toLowerCase()
                                .includes(search.toLowerCase())
                        );
                    }
                    setItems(filteredStock);
                    setOtherParties(responseData.otherParties);
                    setStockLocations(responseData.stockLocations);
                } else {
                    throw new Error("Network response was not ok");
                }
            } catch (err) {
                toast.error("Something went wrong getting items");
            }
        };

        getItems();
    }, [authHeader, isRefresh, search]);

    const editInputs = [
        {
            label: "Name",
            name: "name",
            type: "text",
            placeholder: "Enter Name",
            required: true,
        },
        {
            label: "Barcode",
            name: "barcode",
            type: "text",
            placeholder: "Enter Barcode",
            required: true,
        },
        {
            label: "Price",
            name: "priceNormal",
            type: "number",
            placeholder: "Enter Price",
            required: true,
        },
        {
            label: "Wholesale Price",
            name: "priceWholesale",
            type: "number",
            placeholder: "Enter Price",
            required: true,
        },
        {
            label: "Description",
            name: "description",
            type: "text",
            placeholder: "Enter Description",
            required: true,
            multiline: true,
        },
        {
            label: "Minimum Alarm Quantity",
            name: "minAlarmQuantity",
            type: "number",
            placeholder: "Enter Minimum Alarm Quantity",
            required: true,
        },
    ];

    ///////////////////////////////////////////////////////////////////////////
    /////// New Add Item Code
    ///////////////////////////////////////////////////////////////////////////
    const [isOpenAddItemDialog, setIsOpenAddItemDialog] = useState(false); // Dialog for adding new item

    const handleSubmitAddItemForm = async (formData) => {
        try {
            const response = await fetch(Get_URI("POST"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: authHeader,
                },
                body: JSON.stringify(formData),
            });
            if (response.ok) {
                setIsRefresh(!isRefresh);
                setIsOpenAddItemDialog(false); // Close the dialog after submission
                const data = await response.json();
                const newItem = data.item;
                toast.success("Item added successfully");
                setSelectedItem(newItem.id);
                await asyncDelay(1000); // Delay to allow the new item to be added to the list
                setIsAddItemQuantityOpen(true);
            } else {
                throw new Error("Failed to add new item");
            }
        } catch (error) {
            console.error("Failed to add item:", error);
        }
    };

    const handleOpenAddItemDialog = () => {
        setIsOpenAddItemDialog(true);
    };

    const handleCloseAddItemDialog = () => {
        setIsOpenAddItemDialog(false);
    };

    const handleCloseAll = () => {
        setSelectedItem(null);
        setIsAddItemQuantityOpen(false);
        setIsOpenAddItemDialog(false);
        setIsRefresh(!isRefresh);
    };

    return (
        <Box
            sx={{ width: "100%" }}
            dir={theme.direction === "rtl" ? "rtl" : "ltr"}
        >
            {/* Filter UI */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <TextField
                    label="Name"
                    name="name"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    variant="outlined"
                    size="small" // Make the TextField smaller
                />

                <IconButton color="primary" onClick={applyFilters}>
                    <SearchIcon />
                </IconButton>
            </Box>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                }}
            >
                <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold" }}
                    color="primary"
                    gutterBottom
                >
                    Item List
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOpenAddItemDialog}
                >
                    Add New Item
                </Button>
            </Box>
            <CustomDataGrid
                columns={columns}
                rows={items}
                actions={actions}
                hasEdit={0}
            />
            <CustomFormDialog
                title="New Item"
                fields={addItemFormFields}
                open={isOpenAddItemDialog}
                onClose={handleCloseAddItemDialog}
                formSubmitText="Submit"
                handleSubmitClick={handleSubmitAddItemForm}
            />

            <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this row?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
            {isEditDialogOpen && (
                <EditDialog
                    isOpen={isEditDialogOpen}
                    onClose={() => {
                        setIsEditDialogOpen(false);
                        setSelectedItemToEdit(null);
                        setSelectedItem(null);
                        setIsRefresh(!isRefresh);
                    }}
                    editData={selectedItemToEdit}
                    editInputs={editInputs}
                    submitEdit={updateItem}
                />
            )}
            {isAddItemQuantityOpen && selectedItem && (
                <AddStockDialog
                    item={items.find((item) => selectedItem === item.id)}
                    isOpen={isAddItemQuantityOpen}
                    onClose={handleCloseAll}
                    authHeader={authHeader}
                    stockLocations={stockLocations}
                    otherParties={otherParties}
                    fetchCustomers={() => {
                        setIsRefresh(!isRefresh);
                    }}
                />
            )}

            {isViewStock && (
                <Dialog
                    open={isViewStock}
                    onClose={() => setIsViewStock(false)}
                    maxWidth="lg"
                >
                    <DialogTitle>
                        {items.find((item) => selectedItem === item.id).name}{" "}
                        Stock
                    </DialogTitle>
                    <DialogContent
                        sx={{
                            width: "800px",
                            height: "400px",
                            overflowY: "auto",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px",
                                direction: "ltr",
                                py: "10px",
                            }}
                        >
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Quantity</TableCell>
                                            <TableCell>
                                                Remaining Quantity
                                            </TableCell>
                                            <TableCell>Price</TableCell>
                                            <TableCell>Expiry Date</TableCell>
                                            <TableCell>
                                                Stock Location
                                            </TableCell>
                                            <TableCell>Added By</TableCell>
                                            <TableCell>Other Party</TableCell>
                                            <TableCell>Added At</TableCell>
                                            <TableCell>Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {stockToView?.map((stock) => (
                                            <TableRow>
                                                <TableCell>
                                                    {stock.quantityInitial}
                                                </TableCell>
                                                <TableCell>
                                                    {stock.quantityRemaining}
                                                </TableCell>
                                                <TableCell>
                                                    {stock.pricePurchase}
                                                </TableCell>
                                                <TableCell>
                                                    {new Date(
                                                        stock.expiryDate
                                                    ).toLocaleDateString()}
                                                </TableCell>
                                                <TableCell>
                                                    {stock.stockLocation.name}
                                                </TableCell>
                                                <TableCell>
                                                    {stock.addedBy.name}
                                                </TableCell>
                                                <TableCell>
                                                    {stock.otherParty.name}
                                                </TableCell>
                                                <TableCell>
                                                    {new Date(
                                                        stock.createdAt
                                                    ).toLocaleString()}
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        onClick={() => {
                                                            setSelectedStock(
                                                                stock
                                                            );
                                                            setEditStockForm({
                                                                quantityInitial:
                                                                    stock.quantityInitial,
                                                                pricePurchase:
                                                                    stock.pricePurchase,
                                                                otherPartyId:
                                                                    stock
                                                                        .otherParty
                                                                        ?.id ||
                                                                    "",
                                                            });
                                                            setIsEditStock(
                                                                true
                                                            );
                                                        }}
                                                    >
                                                        <Edit />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Dialog
                                open={isEditStock}
                                onClose={() => setIsEditStock(false)}
                                fullWidth
                                maxWidth="sm"
                            >
                                <DialogTitle>Edit Stock</DialogTitle>
                                <DialogContent
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 2,
                                        mt: 1,
                                    }}
                                >
                                    <TextField
                                        type="number"
                                        label="Initial Quantity"
                                        value={editStockForm.quantityInitial}
                                        onChange={(e) =>
                                            setEditStockForm((p) => ({
                                                ...p,
                                                quantityInitial: e.target.value,
                                            }))
                                        }
                                        size="small"
                                    />
                                    <TextField
                                        type="number"
                                        label="Purchase Price"
                                        value={editStockForm.pricePurchase}
                                        onChange={(e) =>
                                            setEditStockForm((p) => ({
                                                ...p,
                                                pricePurchase: e.target.value,
                                            }))
                                        }
                                        size="small"
                                    />
                                    <TextField
                                        select
                                        label="Other Party"
                                        value={editStockForm.otherPartyId}
                                        onChange={(e) =>
                                            setEditStockForm((p) => ({
                                                ...p,
                                                otherPartyId: e.target.value,
                                            }))
                                        }
                                        size="small"
                                    >
                                        <MenuItem value="">None</MenuItem>
                                        {otherParties.map((op) => (
                                            <MenuItem key={op.id} value={op.id}>
                                                {op.name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Note: Changing Initial Quantity can't
                                        reduce it below already sold amount.
                                        Backend will validate.
                                    </Typography>
                                </DialogContent>
                                <DialogActions>
                                    <Button
                                        onClick={() => {
                                            const itemId = selectedItem; // preserve reference
                                            setIsEditStock(false);
                                            setSelectedStock(null);
                                            // refetch current stock list after closing
                                            fetchStockForItem(itemId, { showDialog: true });
                                        }}
                                        color="inherit"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={async () => {
                                            if (!selectedStock) return;
                                            const payload = {};
                                            if (
                                                editStockForm.quantityInitial !==
                                                ""
                                            )
                                                payload.quantityInitial =
                                                    Number(
                                                        editStockForm.quantityInitial
                                                    );
                                            if (
                                                editStockForm.pricePurchase !==
                                                ""
                                            )
                                                payload.pricePurchase = Number(
                                                    editStockForm.pricePurchase
                                                );
                                            if (
                                                editStockForm.otherPartyId !==
                                                ""
                                            )
                                                payload.otherPartyId = Number(
                                                    editStockForm.otherPartyId
                                                );

                                            try {
                                                const resp = await fetch(
                                                    `${REACT_APP_BACKEND_URL}/stock/${selectedStock.id}`,
                                                    {
                                                        method: "PATCH",
                                                        headers: {
                                                            Authorization:
                                                                authHeader,
                                                            "Content-Type":
                                                                "application/json",
                                                        },
                                                        body: JSON.stringify(
                                                            payload
                                                        ),
                                                    }
                                                );
                                                if (!resp.ok) {
                                                    const errData = await resp
                                                        .json()
                                                        .catch(() => ({}));
                                                    throw new Error(
                                                        errData.error ||
                                                            "Failed to update stock"
                                                    );
                                                }
                                                await resp.json();
                                                toast.success("Stock updated successfully");
                                                const itemId = selectedItem; // capture before reset
                                                setIsEditStock(false);
                                                setSelectedStock(null);
                                                // refetch from backend to show authoritative recalculated values
                                                fetchStockForItem(itemId, { showDialog: true });
                                            } catch (e) {
                                                toast.error(
                                                    e.message ||
                                                        "Error updating stock"
                                                );
                                            }
                                        }}
                                        color="primary"
                                    >
                                        Save
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => {
                                setStockToView(null);
                                setSelectedItem(null);
                                setIsViewStock(false);
                            }}
                            color="error"
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    );
};

export default ItemList;
